import { createSelector } from 'reselect'
import config from 'constants/config'
import { ChertoExperiments } from 'constants/experiments'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import { isAllowedToTriggerExperiment } from 'lib/optimizely/optimizelyUtils'
import { getChertoExperimentVariation } from 'selectors/experimentSelectors'
import { selectIsGuestCheckoutMode } from 'selectors/checkoutSelectors'
import { isSpoofed } from 'selectors/isSpoofed'
import { isExperimentPreview } from 'lib/experiments/preview'

const mobileCheckoutUpsellHideExperiment = ChertoExperiments.croMobileCheckoutHideExperiencesFlights

function configuredMobileCheckoutUpsellHideVariation(): string | undefined {
  const value = config[`CHERTO_${mobileCheckoutUpsellHideExperiment.id}`]
  if (value == null || value === '') return undefined
  return String(value).toLowerCase()
}

/**
 * Who can enter the mobile checkout upsell hide test.
 * Device type comes from the request user agent so the first paint matches SSR.
 * The viewport hook is false on the server and would flicker the steps.
 * Spoofed sessions are excluded deliberately. A local CHERTO_ override does not
 * bypass that. Activation still needs this mobile audience.
 * This does not read the variation. Eligibility has to be true for both arms.
 */
export const getIsMobileCheckoutUpsellHideAudience = createSelector(
  () => config.BRAND === 'luxuryescapes',
  (state: App.State) => state.system.device.deviceType === 'mobile',
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => isSpoofed(state),
  (state: App.State) => selectIsGuestCheckoutMode(state),
  (isLuxuryEscapes, isMobile, postPurchase, spoofed, guest): boolean =>
    isLuxuryEscapes && isMobile && !postPurchase && !spoofed && !guest,
)

/**
 * A local CHERTO_ override forces the variant for any non-spoofed checkout.
 * The device-type check stays on the Cherto assignment, because that is the
 * real audience. The override is what a local session uses to see the variant.
 */
export const getIsMobileCheckoutUpsellHideTreatment = createSelector(
  (state: App.State) => getIsMobileCheckoutUpsellHideAudience(state),
  (state: App.State) => isExperimentPreview(state),
  (state: App.State) => isSpoofed(state),
  (state: App.State) => selectIsGuestCheckoutMode(state),
  (state: App.State) => !!findPostPurchaseCheckout(state.checkout.cart.mode),
  () => config.BRAND === 'luxuryescapes',
  (state: App.State) => isAllowedToTriggerExperiment(
    true,
    state.utm?.content,
    mobileCheckoutUpsellHideExperiment.id,
  ),
  () => configuredMobileCheckoutUpsellHideVariation(),
  (state: App.State) => getChertoExperimentVariation(state, mobileCheckoutUpsellHideExperiment),
  (audience, preview, spoofed, guest, postPurchase, isLuxuryEscapes, canTrigger, configured, variation): boolean => {
    if (preview || !canTrigger || !isLuxuryEscapes || spoofed || guest || postPurchase) return false
    if (configured === 'variant') return true
    return audience && variation === 'variant'
  },
)
